/**
 * Page header component
 * Displays page title, description, and optional breadcrumbs
 */

import { ReactNode } from 'react';
import { Typography, Breadcrumb, Space, Button } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;

interface BreadcrumbItem {
  title: string;
  href?: string;
}

interface PageHeaderProps {
  title: string;
  description?: string;
  breadcrumbs?: BreadcrumbItem[];
  showBack?: boolean;
  backPath?: string;
  extra?: ReactNode;
  className?: string;
}

export function PageHeader({
  title,
  description,
  breadcrumbs,
  showBack,
  backPath,
  extra,
  className,
}: PageHeaderProps) {
  const navigate = useNavigate();

  const handleBack = () => {
    if (backPath) {
      navigate(backPath);
    } else {
      navigate(-1);
    }
  };

  return (
    <div className={`page-header ${className || ''}`}>
      {/* Breadcrumbs */}
      {breadcrumbs && breadcrumbs.length > 0 && (
        <Breadcrumb
          style={{ marginBottom: 8 }}
          items={breadcrumbs.map((item, index) => ({
            key: index,
            title: item.href ? (
              <Link to={item.href}>{item.title}</Link>
            ) : (
              item.title
            ),
          }))}
        />
      )}

      {/* Title row */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 16,
        }}
      >
        <Space align="start">
          {showBack && (
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={handleBack}
              style={{ marginTop: 4 }}
            />
          )}
          <div>
            <Title level={3} style={{ marginBottom: 0 }}>
              {title}
            </Title>
            {description && (
              <Text type="secondary" style={{ marginTop: 4, display: 'block' }}>
                {description}
              </Text>
            )}
          </div>
        </Space>

        {/* Extra content (actions, etc.) */}
        {extra && <div>{extra}</div>}
      </div>
    </div>
  );
}

export default PageHeader;
